import { lazy, Suspense, useEffect, useState } from 'react'

import { GetInTouchOverlayProvider } from '@/components/get-in-touch-overlay'
import { HeroLandingContent } from '@/components/hero-landing-content'
import { HeroSunriseScrollSection } from '@/components/hero-sunrise-scroll-section'
import {
  morningHeaderPresets,
  type MorningHeaderPresetKey,
} from '@/components/morning-header-presets'
import { PageHashNavigation } from '@/components/page-hash-navigation'
import { SiteHeaderNav } from '@/components/site-header-nav'
import type { TechnologyLogosStripVariant } from '@/components/technology-logos-strip'
import { setPageMeta } from '@/lib/seo'
import { cn } from '@/lib/utils'

const TechnologyLogosStrip = lazy(() =>
  import('@/components/technology-logos-strip').then((m) => ({
    default: m.TechnologyLogosStrip,
  })),
)
const ServicesProcessPinBlock = lazy(() =>
  import('@/components/services-process-pin-block').then((m) => ({
    default: m.ServicesProcessPinBlock,
  })),
)
const FaqSection = lazy(() =>
  import('@/components/faq-section').then((m) => ({
    default: m.FaqSection,
  })),
)
const StatsGridSection = lazy(() =>
  import('@/components/stats-grid-section').then((m) => ({
    default: m.StatsGridSection,
  })),
)
const ContactSection = lazy(() =>
  import('@/components/contact-section').then((m) => ({
    default: m.ContactSection,
  })),
)
const MorningFooterAlt = lazy(() =>
  import('@/components/morning-footer-alt').then((m) => ({
    default: m.MorningFooterAlt,
  })),
)

/** Logos below hero: try `'marquee'` | `'inline'` | `'pills'` | `'icons'` | `'rail'` | `'band'`. */
const technologyLogosVariant: TechnologyLogosStripVariant = 'marquee'

/** Header chrome: `'glass'` | `'minimal'` | `'solid'` | `'pillRail'` | `'accentTop'`. */
const morningHeaderActivePreset: MorningHeaderPresetKey = 'accentTop'

const morningHeaderPreset = morningHeaderPresets[morningHeaderActivePreset]

export type MorningLandingColorVariant = 'default' | 'alt'

type MorningLandingProps = {
  /** When true, disables scroll-driven and CSS motion (see `/static-no-animation`). */
  staticNoAnimation?: boolean
  /**
   * Colour staging for the lower page.
   * - `default` — ember contact, ocean-deep footer
   * - `alt` — ocean-deep contact, ember footer
   */
  colorVariant?: MorningLandingColorVariant
}

const morningLandingSurfaces = {
  default: {
    statsBand: 'ember',
    contact: 'ember',
    footer: 'ocean-deep',
  },
  alt: {
    statsBand: 'ocean-deep',
    contact: 'ocean-deep',
    footer: 'ember',
  },
} as const satisfies Record<
  MorningLandingColorVariant,
  {
    statsBand: 'ember' | 'ocean-deep'
    contact: 'ember' | 'ocean-deep'
    footer: 'ocean-deep' | 'ember'
  }
>

export function MorningLanding({
  staticNoAnimation = false,
  colorVariant = 'default',
}: MorningLandingProps) {
  const [sunriseComplete, setSunriseComplete] = useState(false)
  const logosVariant: TechnologyLogosStripVariant = staticNoAnimation
    ? 'inline'
    : technologyLogosVariant
  const surfaces = morningLandingSurfaces[colorVariant]

  useEffect(() => {
    setPageMeta({
      path: '/',
      title: 'Morning Studio | Web Development & Digital Architecture',
      description:
        '100% Australian owned web development agency building custom platforms, enterprise WordPress, and agency white-label solutions.',
    })
  }, [])

  return (
    <GetInTouchOverlayProvider>
      <PageHashNavigation />
      <div
        id="top"
        className={cn(
          'isolate min-h-svh bg-paper-deep font-body-dm-sans text-ink antialiased dark:bg-ocean-deep dark:text-zinc-50',
          staticNoAnimation && 'static-no-animation',
        )}
      >
        <header className="sticky top-0 z-50 bg-transparent">
          <SiteHeaderNav {...morningHeaderPreset} />
        </header>

        <main id="main-content" tabIndex={-1}>
          <HeroSunriseScrollSection
            introText="We build on, and integrate with, the platforms your business already relies on."
            onSunriseComplete={() => setSunriseComplete(true)}
          >
            <HeroLandingContent reveal={sunriseComplete} />
          </HeroSunriseScrollSection>

          <Suspense fallback={null}>
            <TechnologyLogosStrip variant={logosVariant} />

            <ServicesProcessPinBlock staticNoAnimation={staticNoAnimation} />

            <FaqSection />

            <StatsGridSection
              staticNoAnimation={staticNoAnimation}
              bandSurface={surfaces.statsBand}
            />

            <ContactSection surface={surfaces.contact} />
          </Suspense>
        </main>

        <Suspense fallback={null}>
          <MorningFooterAlt surface={surfaces.footer} />
        </Suspense>
      </div>
    </GetInTouchOverlayProvider>
  )
}
